import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Checkbox,
  FormControl,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import ContactEditDialog from "../components/ContactEditDialog";
import CustomerDetailsPanel from "../components/CustomerDetailsPanel";
import ContactViewModel from "../viewmodels/ContactViewModel";
import Contact from "../models/Contacts";
import ContactApiService from "../services/ContactApiService";
import TagService from "../services/TagService";
import { errorMessage } from "../shared/utils/validators";
import appConfig from "../shared/js/app-config";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

const SORT_OPTIONS = [
  {
    key: "newest",
    label: "Created (Newest)",
    sortBy: "created_at",
    sortOrder: "desc",
  },
  {
    key: "oldest",
    label: "Created (Oldest)",
    sortBy: "created_at",
    sortOrder: "asc",
  },
];

const ContactListView = () => {
  const [contactViewModel] = useState(() => new ContactViewModel());
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState('All');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("single"); // "single" or "multiple"
  const [contactToDelete, setContactToDelete] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].sortBy);
  const [sortOrder, setSortOrder] = useState(SORT_OPTIONS[0].sortOrder);
  const [sortKey, setSortKey] = useState("newest");

  function fetch() {
    setLoading(true);
    ContactApiService.getContactsDetails({
      search: searchQuery?.trim(),
      sortBy: sortBy,
      sortOrder: sortOrder,
      tag: selectedTag.trim(),
    }).then((res) => {
      setContacts(res);
      setLoading(false);
    });
  }
  // Sync with ViewModel
  useEffect(() => {
    const updateContacts = () => {
      fetch();
      const updatedContacts = contacts;
      setSelectedContacts(contactViewModel.getSelectedContacts());

      // Auto-select first contact if no contact is currently selected
      if (updatedContacts.length > 0 && !selectedContact) {
        setSelectedContact(updatedContacts[0]);
      }
    };

    updateContacts();
    TagService.getAllTags().then((res) => setAvailableTags(res));
    contactViewModel.onStateChange = updateContacts;

    return () => {
      contactViewModel.onStateChange = null;
    };
  }, [contactViewModel]);
  async function fetchService() {
    await appConfig.fetch();
  }
  useEffect(() => {
    fetchService();
    fetch();
  }, []);

  useEffect(() => {
    fetchService();
    fetch();
  }, [searchQuery?.trim(), sortBy, sortOrder, selectedTag?.trim()]);

  const handleSearchChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    // await contactViewModel.searchContacts(query);
  };

  const handleContactSelection = (contactId) => {
    contactViewModel.toggleContactSelection(contactId);
  };

  const handleContactRowClick = (contact) => {
    setSelectedContact(contact);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      contactViewModel.selectAllContacts();
    } else {
      contactViewModel.clearSelection();
    }
  };

  const handleMoreOptionsClick = (event, contact) => {
    setAnchorEl(event.currentTarget);
    setActiveContact(contact);
  };

  const handleMoreOptionsClose = () => {
    setAnchorEl(null);
    setActiveContact(null);
  };

  const handleEditContact = () => {
    setEditingContact(activeContact);
    setEditDialogOpen(true);
    handleMoreOptionsClose();
  };

  const handleDeleteContact = () => {
    if (activeContact) {
      setContactToDelete(activeContact);
      setDeleteType("single");
      setDeleteDialogOpen(true);
    }
    handleMoreOptionsClose();
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setEditDialogOpen(true);
  };

  const handleSaveContact = async (contactData) => {
    const model = new Contact();

    model.setName(contactData.name);
    model.setPhoneNumber(contactData.phone);
    model.setTags(contactData.tags);
    model.setEmail(contactData.email);
    if (editingContact) {
      try {
        model.setId(editingContact.getId());
        await ContactApiService.updateContact(model);
        setSnackbar({
          open: true,
          message: "Contact updated successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to edit contact",
          severity: "error",
        });
      }
    } else {
      try {
        await ContactApiService.createContact(model);
        setSnackbar({
          open: true,
          message: "Contact added successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to add contact",
          severity: "error",
        });
      }
    }
    fetch();
  };

  const handleDeleteSelected = () => {
    setDeleteType("multiple");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteType === "single" && contactToDelete) {
      try {
        const result = await ContactApiService.deleteContact(
          contactToDelete.getId(),
        );
        setSnackbar({
          open: true,
          message: result.message || "Contact deleted successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to delete contact",
          severity: "error",
        });
      }
    } else if (deleteType === "multiple") {
      const result = await contactViewModel.deleteSelectedContacts();
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Selected contacts deleted successfully",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || "Failed to delete contacts",
          severity: "error",
        });
      }
    }
    setDeleteDialogOpen(false);
    setContactToDelete(null);
    fetch();
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const getTagColor = (color) => {
    return color || "#757575";
  };

  // Calculate selection state based on currently visible contacts
  const visibleSelectedCount = contacts.filter(
    (contact) => contact.isSelected,
  ).length;
  const isAllSelected =
    contacts.length > 0 && visibleSelectedCount === contacts.length;
  const isIndeterminate =
    visibleSelectedCount > 0 && visibleSelectedCount < contacts.length;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            width: "100%",
            // borderBottom: "1px solid #e8e8e8",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Left Section - Search */}
            <TextField
              placeholder="Search by name or number"
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }}
                  />
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  fontSize: "0.875rem",
                  fontFamily: "Poppins, sans-serif",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    borderColor: "#1976d2",
                    boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            />

            {/* Right Section - Filter and Add Button */}
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={sortKey}
                  onChange={(e) => {
                    const selected = SORT_OPTIONS.find(
                      (opt) => opt.key === e.target.value,
                    );
                    setSortKey(selected.key);
                    setSortBy(selected.sortBy);
                    setSortOrder(selected.sortOrder);
                  }}
                  displayEmpty
                  startAdornment={
                    <FilterIcon
                      sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }}
                    />
                  }
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      paddingLeft: 0.5,
                    },
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Tag Filter Dropdown */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  displayEmpty
                  startAdornment={
                    <FilterIcon
                      sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }}
                    />
                  }
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: "#fafafa",
                    border: "1px solid #e0e0e0",
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#d0d0d0",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "#fff",
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      paddingLeft: 0.5,
                    },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.875rem" }}>
                    All
                  </MenuItem>
                  {availableTags.map((tag) => {
                    const color = getTagColor(tag.getColor());

                    return (
                      <MenuItem
                        key={tag.getId()}
                        value={tag.getName()}
                        sx={{
                          fontSize: "0.875rem",
                          color: color, // text color
                          "&:hover": {
                            backgroundColor: `${color}20`, // 20% opacity hover
                          },
                        }}
                      >
                        {tag.getName()}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Add Contact Button */}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={handleAddContact}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  px: 2,
                  py: 0.75,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                  border: "1px solid rgba(25, 118, 210, 0.1)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Contact
              </Button>
            </Box>
          </Box>
          {/* Bulk Actions - Show below the main row when contacts are selected */}
          {selectedContacts.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedContacts.length} contact(s) selected
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDeleteSelected}
                sx={{ borderRadius: 2 }}
              >
                Delete Selected
              </Button>
            </Box>
          )}
        </Paper>
        {/* ===== EMPTY CONTACTS PLACEHOLDER ===== */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!loading && contacts.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              color: "#9ca3af",
              py: 6,
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 72, mb: 1.5 }} />

            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                mb: 0.5,
                fontFamily: "Poppins, sans-serif",
                color: "#374151",
              }}
            >
              No contacts found
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                maxWidth: 300,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Add a contact or adjust your search to see results
            </Typography>
            {/* Add Contact Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddContact}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 2,
                py: 0.75,
                mt: 3,
                fontSize: "0.875rem",
                fontWeight: 500,
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                  boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Contact
            </Button>
          </Box>
        )}

        {/* Table */}
        {!loading && contacts.length > 0 && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              width: "100%",
              borderRadius: 0,
              "& .MuiTable-root": {
                borderCollapse: "separate",
                borderSpacing: 0,
                width: "100%",
              },
            }}
          >
            <Table stickyHeader sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                    }}
                  >
                    {/* <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    sx={{ color: "#666" }}
                  /> */}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Phone Number
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Tags
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow
                    key={contact.getId()}
                    onClick={() => handleContactRowClick(contact)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                      backgroundColor:
                        selectedContact?.id === contact.getId()
                          ? "#e3f2fd"
                          : "transparent",
                    }}
                  >
                    <TableCell padding="checkbox">
                      {/* <Checkbox
                      checked={contact.isSelected}
                      onChange={() => handleContactSelection(contact.getId())}
                      sx={{ color: "#666" }}
                    /> */}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "#1976d2",
                            fontSize: "0.875rem",
                          }}
                        >
                          {contact.avatar ? (
                            <img src={contact.avatar} alt={contact.getName()} />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              fontFamily: "Poppins, sans-serif",
                            }}
                          >
                            {contact.getName()}
                          </Typography>
                          {contact.getEmail() && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: "Poppins, sans-serif" }}
                            >
                              {contact.getEmail()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "Poppins, sans-serif" }}
                      >
                        {contact.getPhoneNumber()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {contact.getTags().map((tag) => (
                          <Chip
                            key={tag.getId()}
                            label={tag.getName()}
                            size="small"
                            sx={{
                              backgroundColor: `${getTagColor(tag.getColor())}15`,
                              color: getTagColor(tag.getColor()),
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) =>
                          handleMoreOptionsClick(event, contact)
                        }
                        sx={{ color: "#666" }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* More Options Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMoreOptionsClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              minWidth: 150,
            },
          }}
        >
          <MenuItem onClick={handleEditContact} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleDeleteContact}
            sx={{ gap: 1, color: "#d32f2f" }}
          >
            <DeleteIcon fontSize="small" />
            Delete
          </MenuItem>
        </Menu>

        {/* Edit Dialog */}
        <ContactEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          contact={editingContact}
          onSave={handleSaveContact}
          availableTags={availableTags}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#d32f2f" }}>
              Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <DialogContentText id="delete-dialog-description">
              {deleteType === "single" && contactToDelete ? (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{contactToDelete.name}</strong>?
                  <br />
                  <br />
                  This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{selectedContacts.length} selected contact(s)</strong>
                  ?
                  <br />
                  <br />
                  This action cannot be undone.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 3,
                background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)",
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Customer Details Panel */}
      <CustomerDetailsPanel
        selectedContact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onUpdateContact={handleSaveContact}
        onRefresh={() => fetch()}
      />
    </Box>
  );
};

export default ContactListView;
